import React from "react";
import Header from "@/components/Header";
import Head from "next/head";
import { generateNextSeo } from "next-seo/pages";
import Footer from "@/components/Footer";
import Container from "@/components/blocks/Container";
import Link from "next/link";
import {
  RocketLaunchIcon,
  ServerIcon,
  BellAlertIcon,
  GlobeAltIcon,
  WrenchScrewdriverIcon,
  QuestionMarkCircleIcon,
  CodeBracketIcon,
  CommandLineIcon,
  GiftIcon,
} from "@heroicons/react/24/outline";
import { docsColors, docsComponents } from "@/lib/docs-theme";

const documentationSections = [
  {
    title: "Getting Started",
    description: "Learn the basics of Uptime Monitor and get your first monitor running",
    icon: RocketLaunchIcon,
    links: [{ title: "Getting Started Guide", href: "/docs/getting-started" }],
  },
  {
    title: "Monitor Types",
    description: "Explore different monitoring options and their configurations",
    icon: ServerIcon,
    links: [
      { title: "All Monitor Types", href: "/docs/monitors" },
      { title: "HTTP/HTTPS Monitoring", href: "/docs/monitors/http" },
      { title: "TCP Port Monitoring", href: "/docs/monitors/tcp" },
      { title: "Ping Monitoring", href: "/docs/monitors/ping" },
    ],
  },
  {
    title: "Notifications & Integrations",
    description: "Configure alerts and connect with your favorite tools",
    icon: BellAlertIcon,
    links: [
      { title: "All Notification Methods", href: "/docs/notifications" },
      { title: "Email Notifications", href: "/docs/notifications/email" },
      { title: "Webhooks", href: "/docs/notifications/webhooks" },
      { title: "Slack", href: "/docs/notifications/slack" },
      { title: "Discord", href: "/docs/notifications/discord" },
      { title: "PagerDuty", href: "/docs/notifications/pagerduty" },
      { title: "incident.io", href: "/docs/notifications/incident-io" },
      { title: "Opsgenie", href: "/docs/notifications/opsgenie" },
      { title: "Zendesk", href: "/docs/notifications/zendesk" },
    ],
  },
  {
    title: "Status Pages",
    description: "Create public status pages to keep your users informed",
    icon: GlobeAltIcon,
    links: [{ title: "Status Pages", href: "/docs/status-pages" }],
  },
  {
    title: "API Reference",
    description: "Integrate Uptime Monitor with your applications using our REST API",
    icon: CodeBracketIcon,
    links: [
      { title: "API Overview", href: "/docs/api" },
      { title: "Authentication", href: "/docs/api/authentication" },
      { title: "Contacts API", href: "/docs/api/contacts" },
      { title: "Monitors API", href: "/docs/api/monitors" },
      { title: "Incidents API", href: "/docs/api/incidents" },
      { title: "Status Pages API", href: "/docs/api/status-pages" },
      { title: "Rate Limiting", href: "/docs/api/rate-limiting" },
      // TODO: Uncomment when pages are ready
      // { title: "Code Examples", href: "/docs/api/examples" },
    ],
  },
  {
    title: "CLI & Infrastructure as Code",
    description: "Manage your monitoring from the terminal, AI assistant, or with Terraform",
    icon: CommandLineIcon,
    links: [
      { title: "CLI Tool", href: "/docs/cli" },
      { title: "Claude Code Skill", href: "/docs/claude-code-skill" },
      { title: "Terraform Provider", href: "/docs/terraform" },
    ],
  },
  {
    title: "Best Practices",
    description: "Optimize your Uptime Monitor setup for effective monitoring",
    icon: WrenchScrewdriverIcon,
    links: [{ title: "Best Practices", href: "/docs/best-practices" }],
  },
  {
    title: "Referral Program",
    description: "Turn your professional network into a revenue stream",
    icon: GiftIcon,
    links: [{ title: "Referral Program", href: "/docs/referral" }],
  },
  {
    title: "Troubleshooting",
    description: "Find solutions to common issues and get help",
    icon: QuestionMarkCircleIcon,
    links: [{ title: "Troubleshooting Guide", href: "/docs/troubleshooting" }],
  },
];

export default function DocsIndexPage() {
  return (
    <>
      <Head>
        {generateNextSeo({
          title: "Documentation - Uptime Monitor",
          description:
            "Learn how to use Uptime Monitor's features, set up integrations, and get the most out of our monitoring platform.",
          canonical: "https://uptime-monitor.io/docs",
          openGraph: {
            title: "Documentation - Uptime Monitor",
            description: "Comprehensive guides and documentation for Uptime Monitor.",
            url: "https://uptime-monitor.io/docs",
          },
        })}
      </Head>
      <div className={docsComponents.pageContainer}>
        <Header />
        <main className="flex-1">
          <Container>
            <div className="mx-auto max-w-7xl px-4 py-12">
              {/* Header */}
              <div className="text-center mb-12">
                <h1 className={`text-4xl font-bold ${docsColors.text.heading} mb-4`}>
                  Documentation
                </h1>
                <p className={`text-xl ${docsColors.text.body} max-w-3xl mx-auto`}>
                  Everything you need to know about monitoring your websites, APIs, and services
                  with Uptime Monitor
                </p>
              </div>

              {/* Search Bar (placeholder for future implementation) */}
              {/* <div className="max-w-2xl mx-auto mb-12">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search documentation..."
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg py-3 px-4 pl-12 text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled
                  />
                  <MagnifyingGlassIcon className="absolute left-4 top-3.5 h-5 w-5 text-gray-400" />
                  <span className="absolute right-4 top-3.5 text-xs text-gray-500">Coming soon</span>
                </div>
              </div> */}

              {/* Main Documentation Sections */}
              <div className="grid gap-8 mb-12">
                {documentationSections.slice(0, 6).map((section) => (
                  <div key={section.title} className={`${docsComponents.card} p-6`}>
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <section.icon className="h-8 w-8 text-blue-500" />
                      </div>
                      <div className="ml-4 flex-1">
                        <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-2`}>
                          {section.title}
                        </h2>
                        <p className={`${docsColors.text.body} mb-4`}>{section.description}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {section.links.map((link) => (
                            <Link
                              key={link.href}
                              href={link.href}
                              className={`${docsColors.link.primary} hover:underline transition-colors`}
                            >
                              {link.title} →
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Best Practices, Referral Program and Troubleshooting Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {documentationSections.slice(6).map((section) => (
                  <div key={section.title} className={`${docsComponents.card} p-6`}>
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <section.icon className="h-8 w-8 text-blue-500" />
                      </div>
                      <div className="ml-4 flex-1">
                        <h2 className={`text-2xl font-semibold ${docsColors.text.heading} mb-2`}>
                          {section.title}
                        </h2>
                        <p className={`${docsColors.text.body} mb-4`}>{section.description}</p>
                        <div className="grid grid-cols-1 gap-3">
                          {section.links.map((link) => (
                            <Link
                              key={link.href}
                              href={link.href}
                              className={`${docsColors.link.primary} hover:underline transition-colors`}
                            >
                              {link.title} →
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Help Section */}
              <div className={`border-t ${docsColors.border.default} pt-8 mt-12`}>
                <p className={`${docsColors.text.subtle} text-center`}>
                  Still have questions? Reach out to us at{" "}
                  <a
                    href="mailto:support@uptime-monitor.io"
                    className={`${docsColors.link.primary} underline`}
                  >
                    support@uptime-monitor.io
                  </a>
                </p>
              </div>
            </div>
          </Container>
        </main>
        <Footer />
      </div>
    </>
  );
}
